<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Platform Dashboard</h1>
        <div class="gl-breadcrumb">
          <router-link to="/" class="gl-breadcrumb-link">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span class="gl-breadcrumb-link">Super Admin</span>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Platform Dashboard</span>
        </div>
      </div>
    </div>

    <!-- ── Skeleton ── -->
    <div v-if="loading">
      <div class="gl-stats-grid">
        <div v-for="i in 8" :key="i" class="gl-stat-card">
          <div class="gl-skel" style="width:40px;height:40px;border-radius:10px;flex-shrink:0"></div>
          <div style="flex:1">
            <div class="gl-skel" style="height:11px;width:70%;border-radius:6px;margin-bottom:8px"></div>
            <div class="gl-skel" style="height:22px;width:55%;border-radius:6px;margin-bottom:6px"></div>
            <div class="gl-skel" style="height:10px;width:40%;border-radius:6px"></div>
          </div>
        </div>
      </div>
      <div class="gl-bottom-grid">
        <div class="gl-card">
          <div class="gl-card-header-bar"><div class="gl-skel" style="height:14px;width:160px;border-radius:6px"></div></div>
          <div class="gl-card-body-pad">
            <div v-for="i in 4" :key="i" style="margin-bottom:18px">
              <div class="gl-skel" style="height:12px;width:80%;border-radius:6px;margin-bottom:7px"></div>
              <div class="gl-skel" style="height:6px;border-radius:100px"></div>
            </div>
          </div>
        </div>
        <div class="gl-card">
          <div class="gl-card-header-bar"><div class="gl-skel" style="height:14px;width:140px;border-radius:6px"></div></div>
          <div class="gl-tbl-wrap">
            <div v-for="i in 5" :key="i" class="gl-skel-row">
              <div class="gl-skel" style="height:13px;flex:2;border-radius:6px"></div>
              <div class="gl-skel" style="height:13px;flex:1;border-radius:6px"></div>
              <div class="gl-skel" style="height:20px;width:54px;border-radius:20px"></div>
              <div class="gl-skel" style="height:13px;flex:1;border-radius:6px"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="gl-card">
        <div class="gl-card-header-bar"><div class="gl-skel" style="height:14px;width:200px;border-radius:6px"></div></div>
        <div class="gl-tbl-wrap">
          <div v-for="i in 5" :key="i" class="gl-skel-row">
            <div class="gl-skel" style="height:13px;flex:1.5;border-radius:6px"></div>
            <div class="gl-skel" style="height:13px;flex:1;border-radius:6px"></div>
            <div class="gl-skel" style="height:13px;flex:1;border-radius:6px"></div>
            <div class="gl-skel" style="height:13px;flex:0.8;border-radius:6px"></div>
            <div class="gl-skel" style="height:13px;flex:0.7;border-radius:6px"></div>
            <div class="gl-skel" style="height:20px;width:64px;border-radius:20px"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Content ── -->
    <div v-if="!loading">

      <!-- Stats grid: 8 cards -->
      <div class="gl-stats-grid">
        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-teal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-4 0v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Companies</div>
            <div class="gl-stat-value">{{ stats.companies?.total || 0 }}</div>
            <div class="gl-stat-sub">{{ stats.companies?.active || 0 }} Active</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Customers</div>
            <div class="gl-stat-value">{{ stats.customers?.total || 0 }}</div>
            <div class="gl-stat-sub">{{ stats.customers?.new_this_month || 0 }} new this month</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Monthly Revenue</div>
            <div class="gl-stat-value">RM {{ formatCurrency(stats.revenue?.this_month) }}</div>
            <div class="gl-stat-sub gl-sub-green">RM {{ formatCurrency(stats.revenue?.today) }} today</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-amber">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">MRR</div>
            <div class="gl-stat-value">RM {{ formatCurrency(stats.subscriptions?.mrr) }}</div>
            <div class="gl-stat-sub">{{ stats.subscriptions?.active || 0 }} active subs</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-teal">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Orders Today</div>
            <div class="gl-stat-value">{{ stats.orders?.today || 0 }}</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Orders This Month</div>
            <div class="gl-stat-value">{{ stats.orders?.this_month || 0 }}</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-amber">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Trialing Subscriptions</div>
            <div class="gl-stat-value">{{ stats.subscriptions?.trialing || 0 }}</div>
          </div>
        </div>

        <div class="gl-stat-card">
          <div class="gl-stat-icon gl-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
          </div>
          <div>
            <div class="gl-stat-label">Total Wallet Balance</div>
            <div class="gl-stat-value">RM {{ formatCurrency(stats.wallet?.total_balance) }}</div>
          </div>
        </div>
      </div>

      <!-- Subscriptions by Plan + Recent Companies -->
      <div class="gl-bottom-grid">
        <div class="gl-card">
          <div class="gl-card-header-bar">Subscriptions by Plan</div>
          <div class="gl-card-body-pad">
            <div v-if="stats.subscriptions?.by_plan?.length">
              <div v-for="(plan, i) in stats.subscriptions.by_plan" :key="i" class="gl-plan-row">
                <div class="gl-plan-top">
                  <span class="gl-plan-name">{{ plan.plan_name }}</span>
                  <span class="gl-plan-count">{{ plan.count }}</span>
                </div>
                <div class="gl-progress-track">
                  <div class="gl-progress-bar" :style="{ width: getPlanPercentage(plan.count) + '%' }"></div>
                </div>
              </div>
            </div>
            <div v-else class="gl-empty-msg">No subscription data available</div>
          </div>
        </div>

        <div class="gl-card">
          <div class="gl-card-header-bar">Recent Companies</div>
          <div class="gl-tbl-scroll">
            <table class="gl-tbl">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="company in stats.recent_companies" :key="company.id">
                  <td>
                    <router-link :to="`/super-admin/companies/${company.id}`" class="gl-link">{{ company.name }}</router-link>
                  </td>
                  <td class="gl-td-muted">{{ company.plan }}</td>
                  <td>
                    <span class="gl-badge" :class="company.is_active ? 'gl-badge-active' : 'gl-badge-inactive'">
                      {{ company.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td class="gl-td-muted">{{ formatDate(company.created_at) }}</td>
                </tr>
                <tr v-if="!stats.recent_companies?.length">
                  <td colspan="4" class="gl-empty-msg" style="text-align:center;padding:28px">No companies yet</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Recent Orders -->
      <div class="gl-card">
        <div class="gl-card-header-bar">Recent Orders <span class="gl-header-sub">Platform-wide</span></div>
        <div class="gl-tbl-scroll">
          <table class="gl-tbl" style="min-width:780px">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Company</th>
                <th>Outlet</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in stats.recent_orders" :key="order.id">
                <td class="gl-td-mono">{{ order.order_number }}</td>
                <td>{{ order.customer }}</td>
                <td class="gl-td-muted">{{ order.company }}</td>
                <td class="gl-td-muted">{{ order.outlet }}</td>
                <td class="gl-td-bold">RM {{ formatCurrency(order.total) }}</td>
                <td><span class="gl-badge" :class="getStatusClass(order.status)">{{ order.status }}</span></td>
                <td><span class="gl-badge" :class="getPaymentClass(order.payment_status)">{{ order.payment_status }}</span></td>
                <td class="gl-td-muted">{{ formatDate(order.created_at) }}</td>
              </tr>
              <tr v-if="!stats.recent_orders?.length">
                <td colspan="8" class="gl-empty-msg" style="text-align:center;padding:28px">No orders yet</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div><!-- /v-if !loading -->
  </div>
</template>

<script>
import { superAdminDashboardApi } from '@/services/superAdminApi';

export default {
  name: 'PlatformDashboard',
  data() {
    return { loading: false, stats: {} };
  },
  created() { this.fetchStats(); },
  methods: {
    async fetchStats() {
      this.loading = true;
      try {
        const res = await superAdminDashboardApi.getStats();
        if (res.data.success) this.stats = res.data.data;
      } catch (e) { console.error('Failed to fetch dashboard stats:', e); }
      finally { this.loading = false; }
    },
    formatCurrency(v) {
      if (!v) return '0.00';
      return parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    getPlanPercentage(count) {
      const total = this.stats.subscriptions?.active || 1;
      return Math.min((count / total) * 100, 100);
    },
    getStatusClass(s) {
      const m = { pending:'gl-badge-pending', confirmed:'gl-badge-confirmed', preparing:'gl-badge-preparing', ready:'gl-badge-ready', completed:'gl-badge-active', cancelled:'gl-badge-inactive' };
      return m[s] || 'gl-badge-muted';
    },
    getPaymentClass(s) {
      const m = { paid:'gl-badge-active', pending:'gl-badge-pending', failed:'gl-badge-inactive', refunded:'gl-badge-info' };
      return m[s] || 'gl-badge-muted';
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 28px 32px 48px; }
.gl-page-header { margin-bottom: 24px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.gl-breadcrumb-link { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb-link:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Stats grid ── */
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon { width: 42px; height: 42px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-icon-teal  { background: rgba(144,12,39,.12); color: #900C27; }
.gl-icon-blue  { background: rgba(59,130,246,.12); color: #3b82f6; }
.gl-icon-green { background: rgba(22,163,74,.12);  color: #16a34a; }
.gl-icon-amber { background: rgba(245,158,11,.12); color: #d97706; }
.gl-stat-label { font-size: 11px; font-weight: 600; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.gl-stat-value { font-size: 20px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; margin-bottom: 2px; }
.gl-stat-sub { font-size: 11px; color: #8a9e9a; }
.gl-sub-green { color: #16a34a; font-weight: 600; }

/* ── Bottom grid ── */
.gl-bottom-grid { display: grid; grid-template-columns: 340px 1fr; gap: 16px; margin-bottom: 16px; align-items: stretch; }

/* ── Cards ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-card:last-child { margin-bottom: 0; }
.gl-bottom-grid > .gl-card { margin-bottom: 0; height: 100%; }
.gl-card-header-bar { padding: 16px 20px 14px; font-size: 14px; font-weight: 700; color: #1a2e2b; border-bottom: 1px solid #f0f3f2; display: flex; align-items: center; gap: 8px; }
.gl-header-sub { font-size: 11.5px; font-weight: 400; color: #8a9e9a; }
.gl-card-body-pad { padding: 18px 20px; }

/* ── Subscriptions by plan ── */
.gl-plan-row { margin-bottom: 16px; }
.gl-plan-row:last-child { margin-bottom: 0; }
.gl-plan-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.gl-plan-name { font-size: 13px; color: #2a3d3a; font-weight: 500; }
.gl-plan-count { font-size: 12px; font-weight: 700; padding: 2px 8px; background: #e0f0ec; color: #1a5248; border-radius: 20px; }
.gl-progress-track { height: 6px; background: #f0f3f2; border-radius: 6px; overflow: hidden; }
.gl-progress-bar { height: 100%; background: linear-gradient(90deg, #900C27, #3d9e8c); border-radius: 6px; transition: width .4s ease; }

/* ── Table ── */
.gl-tbl-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.gl-tbl { width: 100%; border-collapse: collapse; }
.gl-tbl thead th { font-size: 11px; font-weight: 700; color: #8a9e9a; text-transform: uppercase; letter-spacing: .4px; padding: 10px 20px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-tbl tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-tbl tbody tr:last-child { border-bottom: none; }
.gl-tbl tbody tr:hover { background: #f9fbfa; }
.gl-tbl tbody td { padding: 13px 20px; font-size: 13px; color: #2a3d3a; }
.gl-td-muted { color: #6b7f7b !important; }
.gl-td-bold { font-weight: 700; color: #1a2e2b !important; }
.gl-td-mono { font-family: 'Menlo','Consolas',monospace; font-size: 12px; color: #900C27 !important; font-weight: 600; }
.gl-link { color: #900C27; font-weight: 600; text-decoration: none; }
.gl-link:hover { text-decoration: underline; }

/* ── Badges ── */
.gl-badge { display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; text-transform: capitalize; white-space: nowrap; }
.gl-badge-active    { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive  { background: #fce7e7; color: #dc2626; }
.gl-badge-pending   { background: #fef3c7; color: #92400e; }
.gl-badge-confirmed { background: #dbeafe; color: #1d4ed8; }
.gl-badge-preparing { background: #ede9fe; color: #6d28d9; }
.gl-badge-ready     { background: #e0f0ec; color: #1a5248; }
.gl-badge-info      { background: #dbeafe; color: #1d4ed8; }
.gl-badge-muted     { background: #f0f3f2; color: #6b7f7b; }

/* ── Empty ── */
.gl-empty-msg { font-size: 13px; color: #a0b5b0; padding: 24px 0; text-align: center; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-tbl-wrap { padding: 0; }
.gl-skel-row { display: flex; align-items: center; gap: 16px; padding: 14px 20px; border-bottom: 1px solid #f5f7f6; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-header { margin-bottom: 16px; }
  .gl-breadcrumb { display: none; }

  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 14px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-icon { width: 36px; height: 36px; border-radius: 9px; }
  .gl-stat-value { font-size: 17px; }

  .gl-bottom-grid { grid-template-columns: 1fr; gap: 0; }
  .gl-tbl thead th { padding: 9px 14px; }
  .gl-tbl tbody td { padding: 11px 14px; }
  .gl-skel-row { padding: 12px 14px; gap: 10px; }
  .gl-card-header-bar { padding: 14px 16px 12px; }
  .gl-card-body-pad { padding: 14px 16px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb-link { color: #5a7470; }
body.dark-only .gl-stat-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-stat-value { color: #e0eded; }
body.dark-only .gl-stat-label { color: #5a7470; }
body.dark-only .gl-stat-sub { color: #5a7470; }
body.dark-only .gl-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-card-header-bar { color: #e0eded; border-bottom-color: #2a3d3a; }
body.dark-only .gl-plan-name { color: #c0d4d0; }
body.dark-only .gl-progress-track { background: #2a3d3a; }
body.dark-only .gl-tbl thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-tbl tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-tbl tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-tbl tbody td { color: #c0d4d0; }
body.dark-only .gl-td-muted { color: #5a7470 !important; }
body.dark-only .gl-td-bold { color: #e0eded !important; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-skel-row { border-bottom-color: #222f2d; }
body.dark-only .gl-empty-msg { color: #5a7470; }
</style>
